import { useQuery } from "@apollo/client";
import RHHeartIcon from "@RHCommerceDev/icon-rh-heart";
import analyticsLoader from "@RHCommerceDev/analytics/loader";
import { cn } from "@RHCommerceDev/utils/cn";
import RHLogo from "@RHCommerceDev/component-rh-logo";
import NavigationLink from "@RHCommerceDev/container-inline-header/NavigationLink";
import CatalogNavigationDetails from "container-rhr-header/RHRCatalogNavigationDetails";
import { querySaleCatalogNavigation } from "@RHCommerceDev/graphql-client/queries/navigation";
import useBrand from "@RHCommerceDev/hooks-use-brand/useBrand";
import useLocale from "@RHCommerceDev/hooks-use-locale/useLocale";
import { useRhUserAtomValue } from "@RHCommerceDev/hooks/atoms";
import { useAppId } from "@RHCommerceDev/hooks/useAppId";
import { useCountry } from "@RHCommerceDev/hooks/useCountry";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import useSite from "@RHCommerceDev/hooks/useSite";
import RHRArrowIcon from "@RHCommerceDev/icon-arrow-rhr";
import RHChevronIcon from "@RHCommerceDev/icon-chevron";
import React, { FC, useCallback, useMemo } from "react";
import {
  CATALOG_NAV_CONCEPTS_ID,
  catalogLinksToEnable,
  BREAKPOINT_MD,
  BREAKPOINT_LG,
  CATALOG_WISHLIST_NAME,
  CATALOG_REGISTRY_NAME,
  CATALOG_RUGS_NAME,
  CATALOG_DECOR_NAME,
  CATALOG_INTERIOR_DESIGN_NAME,
  CATALOG_SALE_NAME,
  CATALOG_SALE_COLOR,
  TOP_NAV_ITEMS
} from "@RHCommerceDev/utils/constants";
import { useMediaQuery, useTheme } from "@mui/material";
import memoize from "@RHCommerceDev/utils/memoize";
import RHRSaleNavigation from "@RHCommerceDev/container-rhr-header/RHRSaleNavigation";
import { CatalogNavigationProps } from "./types";
import { Theme } from "@mui/material/styles";
import { TailwindTypography as Typography } from "@RHCommerceDev/component-tailwind-typography";
import {
  Popover,
  PopoverContent
} from "@RHCommerceDev/unstyled-ui-components/Popover";
import { PopoverTrigger } from "@RHCommerceDev/unstyled-ui-components/Popover";
import { processEnvServer } from "hooks/useSsrHooks";
import { getReqContext } from "utils/reqContext";
import yn from "yn";

export const CatalogNavigation: FC<CatalogNavigationProps> = ({
  catalogNavigation,
  isDarkMode,
  onChangeSelectedNavItem,
  selectedNavItem,
  onCategoryDrawerClose,
  isAem,
  color,
  disabled,
  showBackToRH = false,
  backToHomeLink,
  backtoRHLabel = "BACK TO RH",
  styles
}) => {
  const brand = useBrand();
  const theme = useTheme();
  const siteId = useSite();
  const env = useEnv();
  const FEATURE_MONTREAL = yn(env?.FEATURE_MONTREAL);
  const isMdToLg = useMediaQuery<Theme>(theme =>
    theme.breakpoints.between(BREAKPOINT_MD, BREAKPOINT_LG)
  );

  const { isConcierge } = useAppId();

  const { userType } = useRhUserAtomValue();
  const locale = useLocale();
  const isFrenchMontreal =
    FEATURE_MONTREAL && (locale === "fr-CA" || locale === "fr-FR");
  const countryCode = useCountry();

  const themeColor = color
    ? color
    : isDarkMode
    ? theme.palette.common.white
    : theme.palette.common.black;

  //fetching sale catalog data
  const req: any = processEnvServer ? getReqContext() : false;
  const pdpLoadPerformance: boolean = processEnvServer
    ? req.cookies.FEATURE_LOAD_PERFORMANCE === "true"
    : env.FEATURE_LOAD_PERFORMANCE;
  const {
    data: { saleCatalogNavigation: saleDataCatalogNavigation } = {} as Query
  } = useQuery<Query>(querySaleCatalogNavigation, {
    variables: {
      filter: "sale",
      siteId,
      locale,
      countryCode,
      nextgenPg: "true"
    },
    skip: processEnvServer && pdpLoadPerformance
  });

  const handleNavItemClick = (
    e: React.SyntheticEvent,
    navElement: NavigationElement
  ) => {
    if (disabled) return;
    e?.stopPropagation?.();

    const target = e?.target as HTMLElement;
    const { id, className } = target;
    const { childCategories, displayName, targetUrl, linkType } = navElement;

    if (!childCategories?.length) {
      document?.body?.dispatchEvent(
        new CustomEvent("cta_click", {
          detail: {
            item: {
              isHeaderNavigation: true,
              topCat: displayName,
              parentCat: displayName,
              cat: targetUrl,
              catURL: targetUrl,
              class: className,
              id: id
            }
          }
        })
      );

      onCategoryDrawerClose();
      window.open(targetUrl || "", linkType === "none" ? "_blank" : "_self");
    } else {
      handleDrawerOpen(e?.currentTarget, navElement);
    }
  };

  const handleDrawerOpen = useCallback(
    (selectedHTMLElement, navElement: NavigationElement) => {
      analyticsLoader(a =>
        a.emitAnalyticsEvent(
          document.querySelector("#spa-root > *") as HTMLElement,
          a.EVENTS.ADD_TO_LOCALSTORAGE.INT_TYPE
        )
      );

      const isSameItem = selectedNavItem?.id === navElement?.id;
      onChangeSelectedNavItem?.(
        isSameItem ? null : navElement,
        isSameItem ? null : selectedHTMLElement
      );
    },
    [onChangeSelectedNavItem, selectedNavItem?.id]
  );

  //topRowItems and bottomRowItems separation
  const { topRowItems, bottomRowItems } = useMemo(() => {
    const top: NavigationElement[] = [];
    const bottom: NavigationElement[] = [];

    catalogNavigation?.childCategories?.forEach(navElement => {
      const displayNameEN = (
        navElement?.displayNameEN ||
        navElement?.displayName ||
        ""
      )
        .toLowerCase()
        .trim();

      if (TOP_NAV_ITEMS.includes(displayNameEN)) {
        top.push(navElement);
      } else {
        bottom.push(navElement);
      }
    });

    return { topRowItems: top, bottomRowItems: bottom };
  }, [catalogNavigation?.childCategories]);

  const renderNavItem = (navElement: NavigationElement) => {
    const id = navElement?.id || "";
    const displayNameEN = (
      navElement?.displayNameEN ||
      navElement?.displayName ||
      ""
    )
      .toLowerCase()
      .trim();

    const isSelected = selectedNavItem?.id == id;

    //Used displayNameEN as unique identifier as it does not change with country and locale
    const category = {
      isWishList: displayNameEN.includes(CATALOG_WISHLIST_NAME),
      isRegistry: displayNameEN === CATALOG_REGISTRY_NAME,
      isRugs: displayNameEN === CATALOG_RUGS_NAME,
      isDecor: displayNameEN === CATALOG_DECOR_NAME,
      isInteriorDesign: displayNameEN === CATALOG_INTERIOR_DESIGN_NAME,
      isSale: displayNameEN === CATALOG_SALE_NAME,
      isTopRowItem: TOP_NAV_ITEMS.includes(displayNameEN)
    };

    // For "SALE" category, use children from sale-specific data and disable its link
    if (category.isSale) {
      navElement = {
        ...navElement,
        childCategories: saleDataCatalogNavigation?.childCategories,
        linkType: "none"
      };
    }

    let cleanTargetUrl = navElement?.targetUrl || "";

    // Normalize targetUrl by trimming after ".jsp", unless it's "/content/category.jsp"
    if (cleanTargetUrl.includes(".jsp")) {
      const jspPath = cleanTargetUrl.substring(
        0,
        cleanTargetUrl.indexOf(".jsp") + 4
      );
      cleanTargetUrl =
        jspPath === "/content/category.jsp" ? cleanTargetUrl : jspPath;
    }

    // Enable link if it's not a sale and the category is in the allowed list or if there are no child categories
    const enableLink =
      (!category.isSale && catalogLinksToEnable.includes(cleanTargetUrl)) ||
      !(navElement?.childCategories?.length ?? 0);

    const labelText = (navElement?.displayName || "")?.replace(/<br>/g, "");

    // Displays nav label with conditional Wish List icon and styles.
    const NavLabel = (
      <NavigationLink
        classes="flex items-end"
        to={navElement.targetUrl}
        style={disabled ? { pointerEvents: "none" } : undefined}
        target={navElement?.linkType === "none" ? "_blank" : "_self"}
        noLink={!enableLink}
      >
        {category.isWishList && <RHHeartIcon color={color} />}
        <Typography
          variant="caption"
          style={{
            color: category.isSale
              ? countryCode === "FR"
                ? themeColor
                : CATALOG_SALE_COLOR
              : themeColor,
            lineHeight: "1.75rem"
          }}
          className={cn(
            "font-normal uppercase",
            isConcierge
              ? "h-[13px] text-[11px]"
              : "h-[18px] font-rhc font-thin leading-normal tracking-wider md:text-[14px] lg:text-base",
            {
              "tracking-[5%] md:text-[11px] lg:text-[12.5px] xl:text-[14px]":
                isFrenchMontreal && !isConcierge
            }
          )}
          dangerouslySetInnerHTML={{ __html: labelText }}
        />
      </NavigationLink>
    );

    // Popover trigger with brand logo and animated arrow indicator
    const BrandPopoverTrigger = (
      <div
        className={cn({
          "-mt-[4px]": !isConcierge,
          "!mt-3": isConcierge
        })}
      >
        <div
          data-testid="catalog-nav-concepts"
          className={`flex h-full w-full cursor-pointer items-center`}
        >
          <div>
            <RHLogo
              onClick={e => e.preventDefault()}
              brand={brand}
              id={"nav-logo-img"}
              primary={isDarkMode ? true : false}
              imgStyle={{
                height: styles?.RHLogoHeight || "10px",
                width: "auto"
              }}
              logoDirName={(styles?.brandDropdownLogosDirName || "") as string}
            />
          </div>
          <div>
            <RHRArrowIcon
              className="ml-2 w-2 rotate-90 text-[8px] transition-transform duration-300 "
              strokeColor={themeColor}
              style={
                isSelected
                  ? { transform: "rotate(270deg)" }
                  : {
                      fontSize: theme.spacing(
                        (styles?.rhLogoArrowFont as number) || 1
                      ),
                      fill: "none"
                    }
              }
            />
          </div>
        </div>
      </div>
    );

    const isLinkHiddenForConcierge =
      isConcierge &&
      (navElement?.targetUrl?.includes("wish-list.jsp") || category.isRegistry);

    const hasNoSubCategories = navElement?.childCategories?.every?.(
      child => !child?.childCategories?.length
    );

    const hasNoSecondarySubCategories = navElement.childCategories?.every(
      child =>
        !child?.childCategories?.length ||
        child.childCategories.every(
          subChild => !subChild?.childCategories?.length
        )
    );

    if (
      isLinkHiddenForConcierge ||
      (navElement?.targetUrl?.includes("gift-registry") &&
        (brand === "TN" || userType === "CONTRACT" || userType === "TRADE"))
    ) {
      return null;
    }

    return (
      <div
        key={id}
        data-navigation-account-item-id={
          navElement.targetUrl === "/my-account/wish-list.jsp"
            ? "wish-list"
            : id
        }
        className={cn(
          "mt-0 flex pt-0 no-underline underline-offset-[5px] hover:underline",
          {
            "hover:text-rh-red": category.isSale && !isDarkMode,
            "hover:text-white": isDarkMode,
            "hover:text-black": !isDarkMode
          }
        )}
      >
        {enableLink ? (
          NavLabel
        ) : (
          <Popover
            open={isSelected}
            onOpenChange={open => {
              if (!open) {
                onCategoryDrawerClose?.();
              }
            }}
          >
            <PopoverTrigger
              className="p-0 text-black data-[state=open]:underline"
              onClick={e => {
                handleNavItemClick(e, navElement);
              }}
            >
              {id === CATALOG_NAV_CONCEPTS_ID ? BrandPopoverTrigger : NavLabel}
            </PopoverTrigger>
            <PopoverContent
              align={
                category.isSale ||
                (isMdToLg &&
                  (category.isRugs ||
                    category.isDecor ||
                    category.isInteriorDesign))
                  ? "end"
                  : "start"
              }
              className={cn("mt-[3px] bg-rh-buff p-10", {
                "w-[680px]": category.isSale && !hasNoSecondarySubCategories,
                "w-[222px]": hasNoSubCategories,
                "w-[516px]":
                  !(category.isSale && !hasNoSecondarySubCategories) &&
                  !hasNoSubCategories
              })}
            >
              {category.isSale ? (
                <RHRSaleNavigation
                  selectedCatalogNavItem={navElement}
                  onCategoryDrawerClose={onCategoryDrawerClose}
                />
              ) : (
                <CatalogNavigationDetails
                  selectedCatalogNavItem={navElement ?? {}}
                  onCategoryDrawerClose={onCategoryDrawerClose}
                  hasNoSubCategories={hasNoSubCategories}
                />
              )}
            </PopoverContent>
          </Popover>
        )}
      </div>
    );
  };

  return (
    <ul
      className={cn(
        "flex flex-nowrap justify-between p-0",
        isAem ? "mx-auto my-3.5 w-1/2" : "my-0"
      )}
      role="menubar"
      aria-label="Main"
    >
      {showBackToRH && backToHomeLink && (
        <span className="absolute -top-[5px] left-[60px] flex cursor-pointer items-center xl:left-[100px]">
          <RHChevronIcon htmlColor={isDarkMode ? "#fff" : "#000"} />
          <a
            href={backToHomeLink}
            className={cn(
              "ml-[5px] hidden no-underline hover:underline md:inline-block",
              isDarkMode
                ? "text-white hover:text-white"
                : "text-black hover:text-black"
            )}
          >
            {backtoRHLabel}
          </a>
        </span>
      )}

      {catalogNavigation?.childCategories?.map(renderNavItem)}
    </ul>
  );
};

export default memoize(CatalogNavigation);
